import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Calendar, Phone, Star } from 'lucide-react';
import { businessInfo } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { EASE } from './motion/Reveal';

const Hero = () => {
  const { t, language } = useLanguage();
  const imageRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: imageRef,
    offset: ['start end', 'end start']
  });
  const imageY = useTransform(scrollYProgress, [0, 1], ['-7%', '7%']);
  const accentY = useTransform(scrollYProgress, [0, 1], ['12%', '-12%']);

  const words = t.hero.headline.split(' ');

  return (
    <section className="relative pt-32 pb-20 bg-white overflow-hidden">
      {/* Elegant decorative elements - silver accent */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gray-300 rounded-full blur-3xl opacity-10 -z-10"></div>
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-gray-400 rounded-full blur-3xl opacity-10 -z-10"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left content */}
          <div className="space-y-8">
            {/* Rating Badge */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
              className="inline-flex items-center space-x-2 bg-black px-4 py-2"
              data-testid="hero-rating-badge"
            >
              <div className="flex items-center space-x-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={16} className="fill-white text-white" />
                ))}
              </div>
              <span className="text-sm font-medium text-white">{businessInfo.rating}</span>
              <span className="text-sm text-gray-300">Google Rating</span>
            </motion.div>

            {/* Kinetic headline - masked word-by-word reveal */}
            <h1
              key={language}
              className="text-5xl sm:text-6xl lg:text-7xl font-light leading-[1.05] text-black"
              data-testid="hero-headline"
            >
              {words.map((word, i) => (
                <span
                  key={i}
                  className="inline-block overflow-hidden align-bottom pb-[0.1em] -mb-[0.1em]"
                >
                  <motion.span
                    className="inline-block will-change-transform"
                    initial={{ y: '115%' }}
                    animate={{ y: '0%' }}
                    transition={{ duration: 0.9, delay: 0.25 + i * 0.055, ease: EASE }}
                  >
                    {word}
                    {i < words.length - 1 ? '\u00A0' : ''}
                  </motion.span>
                </span>
              ))}
            </h1>

            {/* Draw-in rule */}
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 1, delay: 0.9, ease: EASE }}
              className="h-px w-24 bg-black origin-left"
            ></motion.div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
              className="text-lg text-gray-700 leading-relaxed max-w-xl"
            >
              {t.hero.subheadline}
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1, ease: EASE }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <a
                href="https://app.squareup.com/appointments/book/2z9qf4qks9dbay/LFHGNQ373D5BF/start"
                target="_top"
                rel="nofollow"
                data-testid="hero-book-cta"
                className="inline-flex items-center justify-center space-x-2 bg-transparent border-2 border-black text-black px-8 py-4 text-sm font-medium hover:bg-black hover:text-white transition-colors duration-300 active:scale-[0.98]"
                style={{ borderRadius: '0px', minWidth: '210px', height: '60px' }}
              >
                <Calendar size={18} />
                <span>{t.hero.cta1}</span>
              </a>

              <a
                href={`tel:${businessInfo.phone}`}
                data-testid="hero-call-cta"
                className="inline-flex items-center justify-center space-x-2 bg-black text-white px-8 py-4 text-sm font-medium hover:bg-gray-800 transition-colors duration-300 active:scale-[0.98]"
                style={{ borderRadius: '0px', minWidth: '210px', height: '60px' }}
              >
                <Phone size={18} />
                <span>{t.hero.cta2}</span>
              </a>
            </motion.div>

            {/* Business info */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.2, ease: EASE }}
              className="flex flex-wrap gap-6 text-sm text-gray-700 pt-4"
            >
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-black rounded-full"></div>
                <span>{businessInfo.hours}</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-black rounded-full"></div>
                <span>Garland, TX</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-black rounded-full"></div>
                <span>Bilingual Staff</span>
              </div>
            </motion.div>
          </div>

          {/* Right content - Hero Logo with parallax */}
          <div className="relative" ref={imageRef}>
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.1, delay: 0.45, ease: EASE }}
              className="aspect-[4/5] bg-black relative overflow-hidden flex items-center justify-center p-12 border-2 border-gray-200"
            >
              <motion.img
                src="/images/hero/hero-main.png"
                alt="Colombian Body Contour Spa - Expert Aesthetic Care"
                style={{ y: imageY }}
                className="w-full h-auto object-contain scale-110 will-change-transform"
                data-testid="hero-image"
              />
            </motion.div>

            {/* Decorative silver accent with counter-parallax */}
            <motion.div
              style={{ y: accentY }}
              className="absolute -bottom-6 -right-6 w-32 h-32 bg-gray-400 opacity-20 -z-10"
            ></motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
